import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Stack,
  Chip,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import CustomerService from '../../services/customer.service';
import DataTable from '../../components/common/DataTable';
import CustomerQuickSearch from '../../components/common/CustomerQuickSearch';
import { usePagination } from '../../hooks/usePagination';
import {
  findCustomerIdsByServiceNumber,
  appendIdInFilter,
  normalizePhoneDigits,
} from '../../utils/customerSearch';

const CustomerList: React.FC = () => {
  const navigate = useNavigate();
  const { pagination, setPagination, handlePageChange, handlePageSizeChange } = usePagination(25);
  const [customers, setCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [serviceNumber, setServiceNumber] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [generalSearch, setGeneralSearch] = useState('');

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {
        'pagination[page]': pagination.page + 1,
        'pagination[pageSize]': pagination.pageSize,
        sort: 'createdAt:desc',
      };

      if (serviceNumber.trim()) {
        const customerIds = await findCustomerIdsByServiceNumber(serviceNumber);
        if (customerIds.length === 0) {
          setCustomers([]);
          setPagination({ total: 0, pageCount: 0 });
          return;
        }
        appendIdInFilter(params, customerIds);
      }

      if (mobileNumber.trim()) {
        const digits = normalizePhoneDigits(mobileNumber);
        if (digits) {
          params['filters[$or][0][phone][$contains]'] = digits;
          params['filters[$or][1][mobile][$contains]'] = digits;
        }
      }

      if (generalSearch.trim() && !mobileNumber.trim()) {
        params['filters[$or][0][companyName][$containsi]'] = generalSearch.trim();
        params['filters[$or][1][contactPerson][$containsi]'] = generalSearch.trim();
        params['filters[$or][2][email][$containsi]'] = generalSearch.trim();
        params['filters[$or][3][phone][$containsi]'] = generalSearch.trim();
        params['filters[$or][4][mobile][$containsi]'] = generalSearch.trim();
      }

      const response = await CustomerService.getAll(params);
      const customersData = response.data?.data || [];
      const paginationMeta = response.data?.meta?.pagination || {};
      
      setCustomers(customersData);
      setPagination({
        total: paginationMeta.total || 0,
        pageCount: paginationMeta.pageCount || 0,
      });
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, serviceNumber, mobileNumber, generalSearch, setPagination]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    const timer = setTimeout(() => {
      handlePageChange(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [serviceNumber, mobileNumber, generalSearch, handlePageChange]);

  const handleDelete = async (id: number) => {
    if (window.confirm('Delete this customer?')) {
      try {
        await CustomerService.delete(id);
        fetchCustomers();
      } catch (error) {
        console.error('Error deleting customer:', error);
      }
    }
  };

  const clearQuickSearch = () => {
    setServiceNumber('');
    setMobileNumber('');
    setGeneralSearch('');
    setPagination({ page: 0 });
  };

  const columns = [
    { id: 'customerNumber', label: 'Customer #' },
    {
      id: 'companyName',
      label: 'Company',
      format: (value: string) => value || '-',
    },
    { id: 'contactPerson', label: 'Contact Person' },
    { id: 'email', label: 'Email' },
    {
      id: 'phone',
      label: 'Phone',
      format: (value: string) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {value || '-'}
        </Typography>
      ),
    },
    {
      id: 'mobile',
      label: 'Mobile',
      format: (value: string) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {value || '-'}
        </Typography>
      ),
    },
    {
      id: 'vinNumber',
      label: 'VIN Number',
      format: (value: string) => value || '-',
    },
    {
      id: 'plateNumber',
      label: 'Plate Number',
      format: (value: string) => value || '-',
    },
    {
      id: 'servicePackage',
      label: 'Service Package',
      renderCell: ({ value, row }: any) => {
        if (!value) return '-';
        const pkg = value?.data || value?.attributes || value;
        return pkg?.name || pkg?.code || '-';
      },
    },
    {
      id: 'customerType',
      label: 'Type',
      format: (value: string) => (
        <Chip label={value} size="small" color={value === 'business' ? 'primary' : 'default'} />
      ),
    },
    {
      id: 'isActive',
      label: 'Status',
      format: (value: boolean) => (
        <Chip label={value ? 'Active' : 'Inactive'} size="small" color={value ? 'success' : 'default'} />
      ),
    },
  ];

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" fontWeight={700}>Customers</Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => navigate('/customers/create')}
          sx={{ borderRadius: 2, px: 2 }}
        >
          Add Customer
        </Button>
      </Stack>

      <CustomerQuickSearch
        title="Find Customer"
        serviceNumber={serviceNumber}
        mobileNumber={mobileNumber}
        onServiceNumberChange={setServiceNumber}
        onMobileNumberChange={setMobileNumber}
        onClear={clearQuickSearch}
        showGeneralSearch
        generalSearch={generalSearch}
        onGeneralSearchChange={setGeneralSearch}
        generalSearchPlaceholder="Search company, contact, email..."
      />

      <DataTable
        columns={columns}
        data={customers}
        loading={loading}
        page={pagination.page}
        rowsPerPage={pagination.pageSize}
        totalCount={pagination.total}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handlePageSizeChange}
        onView={(row) => navigate(`/customers/${row.id || row.attributes?.id}`)}
        onEdit={(row) => navigate(`/customers/${row.id || row.attributes?.id}/edit`)}
        onDelete={handleDelete}
      />
    </Box>
  );
};

export default CustomerList;
